import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { UtilFns } from '@utils';
import { ApprovalTree } from '@models';
import { ApprovalTreesComponent } from './approval-trees.component';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { UtilDialogService } from '@core';

export interface IApprovalTreeDialogParams {
  parent: ApprovalTreesComponent;
  approvalTree: ApprovalTree;
  isNew: boolean;
}

@Component({
  selector: 'prox-approval-tree-dialog',
  standalone: false,
  templateUrl: './approval-tree.dialog.component.html',
})
export class ApprovalTreeDialogComponent implements OnInit {
  isPageReady = false;
  approvalTree!: ApprovalTree;
  parent!: ApprovalTreesComponent;
  isNew = false;

  isCheckingDesignation = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<IApprovalTreeDialogParams>,
    public toastr: ToastrService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
  ) {}

  async ngOnInit() {
    const params = this.config.data;
    UtilFns.assertNonNull(params);
    this.parent = params.parent;
    this.approvalTree = params.approvalTree;
    this.isNew = params.isNew;

    this.isPageReady = true;
  }

  public static async openDialog(
    dialogService: DialogService,
    data?: IApprovalTreeDialogParams,
    config?: DynamicDialogConfig<IApprovalTreeDialogParams>,
  ) {
    const ref = dialogService.open(ApprovalTreeDialogComponent, {
      header: data?.isNew ? 'Add Proximity Account Organization Tree' : 'Edit Proximity Account Organization Tree',
      closable: false,
      draggable: true,
      width: '600px',
      focusOnShow: false,
      ...config,
      data: data,
    });
    const r = await firstValueFrom(ref.onClose);
    return r;
  }

  async onSave() {
    if (await this.isValid(true)) {
      await this.dbSaveService.saveSelectedChanges([this.approvalTree]);
      this.toastr.success('Saved', 'Database Activity');
      this.ref.close(this.approvalTree);
    }
  }

  onCancel() {
    this.approvalTree.entityAspect.rejectChanges();
    this.ref.close(null);
  }

  canSave() {
    return this.approvalTree.entityAspect.entityState.isAddedModifiedOrDeleted() && this.approvalTree.name?.trim() && this.approvalTree.description?.trim();
  }

  async isValid(showErrors: boolean) {
    if (!this.approvalTree.name?.trim()) {
      if (showErrors) {
        await this.dialogService.warnDialog('Cannot Save', 'Name is required');
      }
      return false;
    }

    if (!this.approvalTree.description?.trim()) {
      if (showErrors) {
        await this.dialogService.warnDialog('Cannot Save', 'Description is required');
      }
      return false;
    }

    // Check for duplicate names
    const existingTrees = this.parent.approvalTrees || [];
    const isDuplicate = existingTrees.some(
      (t) => t.id !== this.approvalTree.id && t.name?.toLowerCase() === this.approvalTree.name?.toLowerCase(),
    );
    if (isDuplicate) {
      if (showErrors) {
        await this.dialogService.warnDialog('Cannot Save', `An Organization Tree with the name "${this.approvalTree.name}" already exists`);
      }
      return false;
    }

    return true;
  }

  /**
   * Guard 1. A program-specific tree may serve at most ONE program that is accepting new orders,
   * because only such a
   * tree may carry per-Program-User-Group purchase orders, p-cards and ordering time frames —
   * settings that resolve at order time by (ProgramUserGroup x Tree), not by program. On a shared
   * tree, configuring one Program silently re-routes another's purchase orders.
   *
   * The check is server-side: this dialog has no reason to load every program on the account, and
   * the guard protects billing. See Docs/Program-Specific-Org-Trees.md.
   *
   * Guarded in BOTH directions. Demotion is a manual, intentional act — nothing auto-demotes — but
   * releasing a tree that still carries group settings would leave those purchase orders both
   * uneditable and ambiguous, re-creating exactly what this feature removes.
   */
  async onIsProgramSpecificChanged() {

    // A brand-new tree has nothing pointing at it yet, in either direction.
    if (this.isNew) return;

    this.isCheckingDesignation = true;
    try {
      const turningOn = this.approvalTree.isProgramSpecific;
      const blockers = turningOn
        ? await this.parent.dbQueryService.getTreeDesignationBlockers(this.approvalTree.id)
        : await this.parent.dbQueryService.getTreeDemotionBlockers(this.approvalTree.id);
      // The check is done — drop the "Checking..." line before opening a modal the administrator
      // may sit on, or it lingers behind the dialog claiming work that has finished.
      this.isCheckingDesignation = false;
      if (blockers.length === 0) return;

      const detail = blockers
        .map((b) => {
          const sample = b.SampleNames.length > 0 ? `<br/>&nbsp;&nbsp;&bull; ${b.SampleNames.join('<br/>&nbsp;&nbsp;&bull; ')}` : '';
          const more =
            b.Count > b.SampleNames.length && b.SampleNames.length > 0
              ? `<br/>&nbsp;&nbsp;<i>&hellip; and ${b.Count - b.SampleNames.length} more</i>`
              : '';
          return `<b>${b.Category}</b> (${b.Count})${sample}${more}`;
        })
        .join('<br/><br/>');

      // Put the checkbox back where it was and say why.
      this.approvalTree.isProgramSpecific = !turningOn;
      await this.dialogService.okDialog(
        turningOn ? 'Cannot Make This Tree Program-Specific' : 'Cannot Release This Tree',
        turningOn
          ? `This tree cannot be made program-specific while more than one Active Program uses it.<br/><br/>${detail}` +
            `<br/><br/>Retire or move all but one of these Programs first. If they genuinely need to run at the same ` +
            `time, give one of them its own copy of this tree instead.`
          : `This tree cannot be released back to the shared pool yet.<br/><br/>${detail}` +
            `<br/><br/>Clear the group settings, and change any Program that still needs a dedicated tree, first. ` +
            `Released now, those group settings would become both uneditable and ambiguous.`,
      );
    } finally {
      this.isCheckingDesignation = false;
    }
  }

  isReadOnly() {
    return this.parent.isReadOnly();
  }

  cancelLabel() {
    return this.isReadOnly() ? 'Close' : 'Cancel';
  }

  async onShowInsights() {
    await this.dialogService.helpDialog(
      'Organization Tree Insights',
      this.parent,
      undefined,
      undefined,
      undefined,
      'planning-your-tree',
    );
  }
}
